import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Tag, NavMenu, NavMenuLinks, NavMenuTitle, NavSidebarLink, Skeleton } from 'src/common';
import { useTranslation } from 'react-i18next';
import { Translations } from 'src/lib/I18nService/types';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import styles from './index.module.scss';
import { TenderCatName } from 'src/models/procurements/Tender/types';
import { WorkspacePicker, WorkspaceStatusPicker } from '../../Workspace';
import { BidItemType } from '@tendium/prom-types/tender';
import { useBidUrl } from 'src/models/procurements/hooks';
import { useTenderNavData } from 'src/models/procurements/TenderNav/hooks';
import { Paths } from 'src/pages/paths';
import { TagType } from 'src/common/Tag';
import { useLocation, useNavigate } from 'react-router-dom';
import { closestIndex, scrollToCat } from './helper';
import { useWorkspaces } from 'src/models/workspace/hooks';

type CatOffset = { id: string; offset: number };

const Categories: FC = () => {
  const { t } = useTranslation();
  const { t: tenderT } = useTranslation(Translations.procurements);

  const { data, loading } = useTenderNavData();
  const { data: workspaceData } = useWorkspaces();

  const accessToWorkspace = useMemo(() => {
    const myWorkspaces = workspaceData?.filter(ws => ws.isActive).map(ws => ws.id);
    return data?.bid ? myWorkspaces?.includes(data.bid.wsId) : undefined;
  }, [data?.bid, workspaceData]);

  const cats = useMemo(() => data?.cats.tenderCats, [data]);
  const keywordSearchCat = useMemo(() => data?.cats.keywordSearchCat, [data]);
  const biddingCat = useMemo(() => data?.cats.biddingCat, [data]);

  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  const currentHash = useMemo(() => (!!hash ? hash.slice(1) : null), [hash]);

  const updateCurrentCat = useCallback(() => {
    const currentTop = document.getElementById('tender')?.scrollTop ?? 0;

    const catOffsets: CatOffset[] = (cats ?? []).map(cat =>
      cat.id === TenderCatName.Overview
        ? { id: cat.id, offset: 0 }
        : { id: cat.id, offset: document.getElementById(cat.id)?.offsetTop ?? 0 }
    );
    const offsets = catOffsets.map(catOffset => catOffset.offset);
    const closestCat = catOffsets[closestIndex(currentTop, offsets)];
    if (closestCat.id !== currentHash) {
      navigate(`${pathname}#${closestCat.id}`);
    }
  }, [cats, currentHash, navigate, pathname]);

  useEffect(() => {
    if (currentHash !== TenderCatName.KeywordSearch) {
      document.addEventListener('wheel', updateCurrentCat, { passive: true });
    }
    return () => {
      document.removeEventListener('wheel', updateCurrentCat);
    };
  }, [currentHash, updateCurrentCat]);

  const onNavigate = useCallback(
    (category: string, e) => {
      e.currentTarget.blur();
      const catOffset = document.getElementById(category)?.offsetTop;

      if (catOffset) {
        navigate(`${pathname}#${category}`);
        scrollToCat(catOffset);
      }
    },
    [navigate, pathname]
  );

  const bidUrl = useBidUrl(data?.bid?.id ?? '');
  const isBidFlowFeature = useFeatureFlag(FeatureFlag.BiddingWorkflow_Tasks);
  const isBidspacesFeature = useFeatureFlag(FeatureFlag.Bidspaces);
  const isKeywordSearchFeature = useFeatureFlag(FeatureFlag.KeywordSearch);

  return loading ? (
    <Skeleton loading active />
  ) : (
    <>
      <NavMenu>
        <NavMenuTitle>{t('DetailsPage.tender')}</NavMenuTitle>
        <NavMenuLinks>
          {!!cats &&
            !!cats.length &&
            cats.map(category => (
              <NavSidebarLink
                to={data?.isBidding ? `${Paths.TENDER_PAGE.replace(':id', data.id)}#${category.id}` : `#${category.id}`}
                title={tenderT(category.title)}
                icon={{ defaultIcon: category.icon.defaultIcon, activeIcon: category.icon.activeIcon }}
                isActive={currentHash === category.id && !data?.isBidding}
                isExpanded
                onClick={e => onNavigate(category.id, e)}
                key={category.id}
              />
            ))}
        </NavMenuLinks>
      </NavMenu>
      {isKeywordSearchFeature && keywordSearchCat && (
        <NavMenu>
          <NavMenuLinks>
            <NavSidebarLink
              to={
                data?.isBidding
                  ? `${Paths.TENDER_PAGE.replace(':id', data.id)}#${keywordSearchCat.id}`
                  : `#${keywordSearchCat.id}`
              }
              title={tenderT(keywordSearchCat.title)}
              icon={{ defaultIcon: keywordSearchCat.icon.defaultIcon, activeIcon: keywordSearchCat.icon.activeIcon }}
              isActive={currentHash === keywordSearchCat.id && !data?.isBidding}
              isExpanded
              onClick={e => onNavigate(keywordSearchCat.id, e)}
              key={keywordSearchCat.id}
            />
          </NavMenuLinks>
        </NavMenu>
      )}
      {data?.cats && !!isBidspacesFeature && (
        <NavMenu>
          <NavMenuTitle>{data.bid ? t('BidSpaces.bid') : t('BidSpaces.qualify')}</NavMenuTitle>
          {data.bid && bidUrl && biddingCat && (
            <NavMenuLinks>
              <NavSidebarLink
                to={bidUrl}
                title={
                  <>
                    <span>{t('BiddingTool.biddingTool')}</span>
                    <Tag className={styles.tag} type={TagType.New} />
                  </>
                }
                icon={{ defaultIcon: biddingCat.icon.defaultIcon, activeIcon: biddingCat.icon.activeIcon }}
                isActive={data.isBidding}
                isExpanded
                key={biddingCat.id}
                onClick={e => e.currentTarget.blur()}
                isDisabled={!isBidFlowFeature}
                tooltip={t('BiddingTool.disabledFeatureTooltip')}
              />
            </NavMenuLinks>
          )}
          <div className={styles.menuItem}>
            <WorkspacePicker
              originIds={data.bid ? undefined : [data.id]}
              bidIds={data.bid ? [data.bid] : undefined}
              originType={BidItemType.Procurement}
              eventSource={data.isBidding ? 'BiddingTool' : 'TenderMainPage'}
              mode={'full'}
              theme={'select'}
            />
          </div>
          {data.bid && !!accessToWorkspace && (
            <div className={styles.menuItem}>
              <WorkspaceStatusPicker
                bidId={data.bid.id}
                className={styles.wsStatusPicker}
                eventSource="Custom report page (details page)"
              />
            </div>
          )}
        </NavMenu>
      )}
    </>
  );
};

export default Categories;
